import { useAuth, useAuthenticated, useUser } from "@fmc-digital/core-auth";
import { Button, Text } from "@fmc-digital/ui";

/**
 * Success Page
 * Shown after successful authentication
 */
export function SuccessPage() {
  const { isAuthenticated: isAuthenticatedContext, user: userContext, signOut, isLoading } = useAuth();
  // Use Zustand store selectors for reliable state
  const isAuthenticatedStore = useAuthenticated();
  const userStore = useUser();
  
  // Use Zustand store as source of truth for authentication state
  const isAuthenticated = isAuthenticatedStore || isAuthenticatedContext;
  const user = userStore || userContext;

  // Show loading state while checking authentication
  if (isLoading && !isAuthenticated) {
    return (
      <div className="w-full flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-6" />
          <Text variant="bodyLarge">Loading your account...</Text>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="auth-card text-center">
        <Text variant="headlineSmall" className="mb-4">
          Not Authenticated
        </Text>
        <Text variant="bodyMedium" color="secondary" className="mb-6">
          You need to sign in to access this page.
        </Text>
        <a href="/login">
          <Button variant="filled">Sign In</Button>
        </a>
      </div>
    );
  }

  const isLocalhost = window.location.hostname === 'localhost' || 
                      window.location.hostname === '127.0.0.1';

  return (
    <div className="auth-card text-center">
      <div className="mb-6">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
          <svg
            className="w-8 h-8 text-primary"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
      </div>

      <Text variant="headlineMedium" className="mb-2">
        Welcome, {user?.givenName ?? user?.name ?? "User"}!
      </Text>
      <Text variant="bodyMedium" color="secondary" className="mb-6">
        You have successfully signed in to your account.
      </Text>

      {isLocalhost && (
        <div className="mb-6 p-4 bg-surface-container-high rounded-lg text-left">
          <Text variant="bodySmall" className="font-semibold mb-2">
            Development Mode
          </Text>
          <Text variant="bodySmall" color="secondary" className="mb-2">
            Dashboard redirect is disabled in localhost due to cookie domain restrictions.
            Authentication is working correctly - you're fully logged in!
          </Text>
          {user?.email && (
            <Text variant="bodySmall" color="secondary">
              Email: {user.email}
            </Text>
          )}
        </div>
      )}

      <div className="mb-6">
        <Text variant="bodySmall" color="secondary">
          You are now authenticated and can access protected features.
        </Text>
      </div>

      <div className="space-y-3">
        {user && (
          <div className="text-left p-4 bg-surface-container rounded-lg">
            <Text variant="labelMedium" color="secondary" className="mb-2">
              Account Details
            </Text>
            <div className="space-y-1">
              <Text variant="bodySmall">
                <span className="text-on-surface-variant">Email:</span>{" "}
                {user.email}
              </Text>
              <Text variant="bodySmall">
                <span className="text-on-surface-variant">Name:</span>{" "}
                {user.name ?? `${user.givenName} ${user.familyName}`}
              </Text>
            </div>
          </div>
        )}

        <Button
          variant="outlined"
          fullWidth
          onClick={signOut}
          loading={isLoading}
        >
          Sign Out
        </Button>
      </div>
    </div>
  );
}
